import { useEffect, useMemo, useState } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../api";
import ImageUploader from "../components/ImageUploader";

/**
 * Generic CMS page editor.
 *
 * Lists available pages from /admin/page-content (Home, Chi Siamo, etc.)
 * and renders a dynamic form based on the data structure returned by
 * /admin/page-content/{key}. Field types are inferred:
 *   - string                         → <input>
 *   - long string (>120 chars)       → <textarea>
 *   - list of strings                → repeatable text inputs
 *   - list of objects ({k1, k2…})    → repeatable card with sub-inputs
 *
 * The admin can edit ANY field — anything that was hardcoded before is
 * now CMS-managed, with safe fall-back to defaults if a field is empty.
 */
export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [data, setData] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/page-content").then((r) => {
      setPages(r.data);
      if (r.data.length) setActiveKey(r.data[0].key);
    });
  }, []);

  useEffect(() => {
    if (!activeKey) return;
    adminApi.get(`/admin/page-content/${activeKey}`).then((r) => {
      setData(r.data.data);
      setDefaults(r.data.defaults || {});
    });
  }, [activeKey]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/admin/page-content/${activeKey}`, data);
      toast.success("Pagina aggiornata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore di salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    if (confirm("Vuoi ripristinare i testi originali? Le modifiche non salvate andranno perse."))
      setData({ ...defaults });
  };

  const orderedKeys = useMemo(() => (data ? Object.keys(data) : []), [data]);

  return (
    <div data-testid="admin-pages" className="space-y-8">
      <header className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <h1 className="font-display text-4xl tracking-tight">Pagine</h1>
          <p className="mt-2 text-sm text-white/50 max-w-2xl">
            Modifica testi, titoli, descrizioni e altri contenuti delle pagine
            pubbliche. Tutti i campi sono opzionali — quelli vuoti tornano al
            testo di default.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={resetToDefault}
            disabled={!data}
            className="rounded-full border border-white/15 px-4 py-2.5 text-xs hover:bg-white/5"
          >
            Ripristina testi originali
          </button>
          <button
            onClick={save}
            disabled={saving || !data}
            data-testid="page-save"
            className="inline-flex items-center gap-2 rounded-full bg-[#C48F56] text-black px-6 py-2.5 text-sm font-medium hover:bg-[#E5B782] disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Salvataggio…" : "Salva"}
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap border-b border-white/5">
        {pages.map((p) => (
          <button
            key={p.key}
            onClick={() => setActiveKey(p.key)}
            data-testid={`page-tab-${p.key}`}
            className={`px-5 py-3 text-sm rounded-t-lg transition-colors ${
              activeKey === p.key
                ? "bg-[#0F0F14] text-white border-x border-t border-white/10"
                : "text-white/50 hover:text-white"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Form */}
      {data ? (
        <section className="rounded-2xl border border-white/10 bg-[#0A0A0D] p-6 md:p-8 space-y-6">
          {orderedKeys.map((key) => (
            <FieldEditor
              key={key}
              fieldKey={key}
              value={data[key]}
              onChange={(v) => setData((d) => ({ ...d, [key]: v }))}
            />
          ))}
          <p className="text-xs text-white/40 pt-3 border-t border-white/5">
            Tip: usa <code className="text-[#C48F56]">*parola*</code> intorno a una parola per
            evidenziarla in ottone nel titolo.
          </p>
        </section>
      ) : (
        <div className="text-white/40">Caricamento…</div>
      )}
    </div>
  );
}

const inputCls =
  "rounded-lg border border-white/10 bg-[#08080A] px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#C48F56]/40 focus:outline-none w-full";

function humanizeKey(key) {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function FieldEditor({ fieldKey, value, onChange }) {
  // List of strings (e.g. "intro_lines", "model_lines")
  if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
    return (
      <ListStringField label={humanizeKey(fieldKey)} value={value} onChange={onChange} />
    );
  }
  // List of objects (e.g. "faqs": [{q, a}])
  if (Array.isArray(value) && value.every((v) => v && typeof v === "object")) {
    return (
      <ListObjectField label={humanizeKey(fieldKey)} value={value} onChange={onChange} />
    );
  }
  // Long string → textarea
  if (typeof value === "string" && value.length > 120) {
    return (
      <Field label={humanizeKey(fieldKey)}>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className={`${inputCls} resize-none leading-relaxed`}
        />
      </Field>
    );
  }
  // Plain string — auto-detect image/photo/poster fields → use ImageUploader
  if (typeof value === "string") {
    const isImageKey = /(^|_)(image|photo|logo|poster|hero|cover)(s)?$|^(image|photo|logo|poster|hero|cover)_url$/i.test(
      fieldKey
    );
    if (isImageKey) {
      return (
        <Field label={humanizeKey(fieldKey)}>
          <ImageUploader value={value} onChange={onChange} />
        </Field>
      );
    }
    return (
      <Field label={humanizeKey(fieldKey)}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      </Field>
    );
  }
  // Numbers / booleans / null → JSON fallback
  return (
    <Field label={humanizeKey(fieldKey)}>
      <input
        type="text"
        value={value == null ? "" : String(value)}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls}
      />
    </Field>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-[0.2em] text-white/45 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function ListStringField({ label, value, onChange }) {
  const update = (i, v) =>
    onChange(value.map((it, j) => (j === i ? v : it)));
  const add = () => onChange([...value, ""]);
  const remove = (i) => onChange(value.filter((_, j) => j !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-white/45">
          {label}
        </span>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-white"
        >
          <Plus size={12} /> Aggiungi
        </button>
      </div>
      <div className="space-y-2">
        {value.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              value={v}
              onChange={(e) => update(i, e.target.value)}
              className={inputCls}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-white/40 hover:text-red-400"
              title="Rimuovi"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ListObjectField({ label, value, onChange }) {
  const update = (i, sub) =>
    onChange(value.map((it, j) => (j === i ? { ...it, ...sub } : it)));
  const subKeys = value.length ? Object.keys(value[0]) : [];
  const empty = subKeys.reduce((acc, k) => ({ ...acc, [k]: "" }), {});
  const add = () => onChange([...value, empty]);
  const remove = (i) => onChange(value.filter((_, j) => j !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-white/45">
          {label}
        </span>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-white"
        >
          <Plus size={12} /> Aggiungi
        </button>
      </div>
      <div className="space-y-3">
        {value.map((item, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-[#0F0F14] p-4 space-y-2">
            {subKeys.map((sk) => {
              const subVal = item[sk] ?? "";
              const isImage = sk === "image" || sk === "photo" || sk === "logo";
              const isLong = typeof subVal === "string" && subVal.length > 80;
              return (
                <div key={sk}>
                  <span className="block text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mb-1">
                    {humanizeKey(sk)}
                  </span>
                  {isImage ? (
                    <ImageUploader
                      value={subVal}
                      onChange={(v) => update(i, { [sk]: v })}
                    />
                  ) : isLong ? (
                    <textarea
                      value={subVal}
                      onChange={(e) => update(i, { [sk]: e.target.value })}
                      rows={2}
                      className={`${inputCls} resize-none`}
                    />
                  ) : (
                    <input
                      type="text"
                      value={subVal}
                      onChange={(e) => update(i, { [sk]: e.target.value })}
                      className={inputCls}
                    />
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-white/40 hover:text-red-400 text-xs inline-flex items-center gap-1"
            >
              <Trash2 size={12} /> Rimuovi
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
